// avgust-preview — w15 Avgust wedding catalog card.
//
// Mood: late-summer harvest, sage + warm gold. The outer frame is a
// double-hairline ornamental border with a brushed-gold gradient; the
// inner card sits on warm cream paper with a wheat / olive-sprig
// ornament centred above the couple's names. Category eyebrow in Uzbek
// is "TO'Y MAROSIMI" (wedding).
import { Photo, type LayoutCtx } from "./_shared";

export function AvgustPreviewLayout({
  names,
  date,
  venue,
  accent,
  ink,
  bg,
  font,
  photo,
}: LayoutCtx) {
  // Brushed-gold frame — warm cream + olive-gold + cream alternation.
  const FOIL =
    "linear-gradient(135deg, #8E6A2A 0%, #C5A05A 22%, #E9D29B 45%, #C5A05A 70%, #8E6A2A 100%)";

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: FOIL,
        padding: "5%",
        display: "flex",
      }}
    >
      <div
        style={{
          flex: 1,
          background: bg,
          color: ink,
          padding: "8%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          boxShadow: `inset 0 0 0 1px ${accent}55`,
          position: "relative",
        }}
      >
        {/* Eyebrow */}
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 7,
            fontWeight: 700,
            letterSpacing: "0.4em",
            color: accent,
            marginBottom: "5%",
          }}
        >
          TO&apos;Y MAROSIMI
        </div>

        {/* Wheat / olive sprig ornament — two sprigs framing a tiny
            star, with an inner photo medallion if a photo is given. */}
        <div
          style={{
            width: "62%",
            aspectRatio: "1 / 1",
            position: "relative",
            marginBottom: "5%",
          }}
        >
          {/* Outer hairline + sprig SVG */}
          <svg
            viewBox="0 0 120 120"
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
            aria-hidden
          >
            <circle cx="60" cy="60" r="56" fill="none" stroke={accent} strokeOpacity="0.45" strokeWidth="0.8" />
            <circle cx="60" cy="60" r="52" fill="none" stroke={accent} strokeOpacity="0.22" strokeWidth="0.4" />

            {/* Two sprigs arching around the medallion */}
            {[1, -1].map((dir) => (
              <g key={dir} transform={`translate(60 60) scale(${dir} 1)`}>
                <path d="M -40 6 C -42 -10 -36 -26 -22 -38" fill="none" stroke={accent} strokeOpacity="0.8" strokeWidth="1" strokeLinecap="round" />
                {[6, -2, -12, -22, -32].map((y, i) => (
                  <ellipse key={i} cx={-40 + i * 4.5} cy={y} rx="4" ry="1.4" transform={`rotate(${-50 + i * 12} ${-40 + i * 4.5} ${y})`} fill={accent} fillOpacity="0.6" />
                ))}
              </g>
            ))}

            {/* Centre 4-point star */}
            <g transform="translate(60 60)">
              <path
                d="M0 -10 L2 -2 L10 0 L2 2 L0 10 L-2 2 L-10 0 L-2 -2 Z"
                fill={accent}
                fillOpacity="0.9"
              />
            </g>
          </svg>

          {/* Optional inner photo medallion */}
          {photo ? (
            <div
              style={{
                position: "absolute",
                inset: "22%",
                borderRadius: "50%",
                overflow: "hidden",
                boxShadow: `inset 0 0 0 1px ${accent}55`,
              }}
            >
              <Photo
                src={photo}
                style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
                fallback={
                  <div
                    style={{
                      position: "absolute",
                      inset: 0,
                      background: `radial-gradient(circle at 50% 45%, ${accent}33, ${bg})`,
                    }}
                  />
                }
              />
            </div>
          ) : null}
        </div>

        {/* Names */}
        <div
          style={{
            fontFamily: font,
            fontSize: 30,
            lineHeight: 1.05,
            fontWeight: 400,
            color: ink,
          }}
        >
          {names}
        </div>

        <div style={{ flex: 1 }} />

        {/* Bottom caption */}
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6.5,
            fontWeight: 700,
            letterSpacing: "0.32em",
            color: accent,
            marginTop: "4%",
          }}
        >
          TO&apos;Y MAROSIMI · {date}
        </div>
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6,
            fontWeight: 600,
            letterSpacing: "0.28em",
            color: `${ink}99`,
            marginTop: 6,
          }}
        >
          {venue.toUpperCase()}
        </div>
      </div>
    </div>
  );
}
